import { useState, useEffect, useRef, useCallback } from 'react';
import axios, { AxiosStatic } from 'axios';
import { useSpinner } from '@vadiun/react-hooks-legacy';
import useViajesTraslado from '../../viajeTraslado/services/ViajeTrasladoService';
import { TransferReservationBaseFiltersFormType } from '../../viajeTraslado/components/ViajeTrasladoBaseFilters';
import {
  GestionColumnVisibility,
  getDefaultFilters,
  getDefaultColumnVisibility,
  getDefaultSort,
  loadStateFromLocalStorage,
  saveStateToLocalStorage
} from '../utils/gestionStorage';
import { TransferReservationManagementListModel } from '../models/TransferReservationManagementList';

export { columnsLabels, getDefaultFilters } from '../utils/gestionStorage';

// The project's Axios declaration exposes the static interface without a default export.
const cancellableAxios = axios as unknown as AxiosStatic;

export const useGestionReserves = () => {
  const gestionService = useViajesTraslado();
  const handleSpinner = useSpinner();
  const spinnerRef = useRef(handleSpinner);
  spinnerRef.current = handleSpinner;

  const [state, setState] = useState(loadStateFromLocalStorage);
  const serviceRef = useRef(gestionService);
  serviceRef.current = gestionService;
  const [reloadVersion, setReloadVersion] = useState(0);
  const reload = useCallback(
    async () => setReloadVersion((version) => version + 1),
    []
  );
  const [result, setResult] = useState<{
    data: TransferReservationManagementListModel[];
    total: number;
    isLoading: boolean;
    error: Error | undefined;
  }>({ data: [], total: 0, isLoading: true, error: undefined });

  useEffect(() => {
    saveStateToLocalStorage(state);
  }, [state]);

  const filtersKey = JSON.stringify(state.filters);

  useEffect(() => {
    const source = cancellableAxios.CancelToken.source();
    let active = true;
    setResult((previous) => ({
      ...previous,
      isLoading: true,
      error: undefined
    }));

    const request = serviceRef.current
      .getManagementList(
        state.page,
        state.rowsPerPage,
        state.sort.sortBy,
        state.sort.sortDir,
        state.filters,
        source.token
      )
      .then((response) => {
        if (!active) return;
        setResult({
          data: response.data,
          total: response.total,
          isLoading: false,
          error: undefined
        });
      })
      .catch((error) => {
        if (!active || cancellableAxios.isCancel(error)) return;
        setResult((previous) => ({ ...previous, isLoading: false, error }));
      });

    spinnerRef.current(request);

    return () => {
      active = false;
      source.cancel('La consulta de Gestión fue reemplazada.');
    };
    // filtersKey avoids requests caused only by equivalent filter object identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.page,
    state.rowsPerPage,
    state.sort.sortBy,
    state.sort.sortDir,
    filtersKey,
    reloadVersion
  ]);

  const onChangePage = (newPage: number) => {
    if (newPage !== state.page) {
      setState((prev) => ({ ...prev, page: newPage }));
    }
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setState((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 1 }));
  };

  const onFiltersChange = (
    filtersEntry: TransferReservationBaseFiltersFormType
  ) => {
    setState((prev) => ({ ...prev, filters: filtersEntry, page: 1 }));
  };

  const onSearchChange = (search: string) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, search },
      page: 1
    }));
  };

  const onSortChange = (direction: string | null, sortBy: string) => {
    setState((prev) => ({
      ...prev,
      sort: {
        sortDir: direction === 'desc' ? 'DESC' : 'ASC',
        sortBy
      },
      page: 1
    }));
  };

  const setColumnVisibility = (
    newColumnVisibility: GestionColumnVisibility
  ) => {
    setState((prev) => ({ ...prev, columnVisibility: newColumnVisibility }));
  };

  const resetState = () => {
    setState({
      page: 1,
      rowsPerPage: 50,
      sort: getDefaultSort(),
      filters: getDefaultFilters(),
      columnVisibility: getDefaultColumnVisibility()
    });
  };

  return {
    gestionReserves: {
      ...result,
      serverSideOptions: {
        page: state.page - 1,
        count: result.total,
        rowsPerPage: state.rowsPerPage,
        sortOrder: {
          name: state.sort.sortBy,
          direction: state.sort.sortDir.toLowerCase() as 'asc' | 'desc'
        }
      }
    },
    reload,
    filters: state.filters,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    columnVisibility: state.columnVisibility,
    setColumnVisibility,
    resetState,
    onChangePage,
    onChangeRowsPerPage,
    onSortChange,
    onFiltersChange,
    onSearchChange
  };
};
